import { useState } from 'react';
import { Link } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import type { SubscriptionInterval } from "@/types/payment";
import { PlanSelector } from "@/components/payment/PlanSelector";
import { plans } from "@/components/payment/plans";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";

const Payment = () => {
  const { toast } = useToast();
  const [interval, setInterval] = useState<SubscriptionInterval>('month');

  const handleIntervalChange = (checked: boolean) => {
    setInterval(checked ? 'year' : 'month');
  };

  const handleSelectPlan = async (planId: string) => {
    // This is where Stripe integration will go
    /* 
    TODO: Uncomment and implement once Stripe is set up:
    
    try {
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceId: plans[planId].stripeId[interval],
          planId,
          interval,
        }),
      });

      const { url } = await response.json();
      window.location.href = url;
    } catch (error) {
      toast({
        title: "Error",
        description: "Unable to process payment. Please try again.",
        variant: "destructive",
      });
    }
    */

    // Temporary toast notification until Stripe is set up
    toast({
      title: "Stripe Integration Required",
      description: `To process payments for the ${plans[planId].name} plan (${interval}ly), please set up your Stripe account first. This will enable secure payment processing.`,
      variant: "destructive",
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      <nav className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="text-xl font-bold text-gray-900">
              SecureFiles
            </Link>
            
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <Link to="/who-we-are">
                    <NavigationMenuLink className={navigationMenuTriggerStyle()}>
                      Who We Are
                    </NavigationMenuLink>
                  </Link>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <Link to="/support">
                    <NavigationMenuLink className={navigationMenuTriggerStyle()}>
                      Support
                    </NavigationMenuLink>
                  </Link>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <Link to="/login">
                    <NavigationMenuLink className={navigationMenuTriggerStyle()}>
                      Login
                    </NavigationMenuLink>
                  </Link>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>
        </div>
      </nav>

      <div className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Choose Your Plan
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
              Select the perfect plan for your needs. All plans include our core features.
            </p>
            
            <PlanSelector
              interval={interval}
              onIntervalChange={handleIntervalChange}
              onSelectPlan={handleSelectPlan}
              plans={plans}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payment;